import type { CreatePartyEntry } from "@/components/facilities/create-parties-section";
import type { PricingScheduleFormData } from "@/components/facilities/pricing-schedule-section";

export type FacilityCreatePartyPayload = {
  partyType: "account" | "contact";
  accountId?: string;
  contactId?: string;
  partyRole: string;
  isPrimary: boolean;
};

export function buildFacilityCreateParties(
  parties: CreatePartyEntry[],
): FacilityCreatePartyPayload[] {
  return parties.map((party) => ({
    partyType: party.partyType,
    partyRole: party.partyRole,
    isPrimary: party.isPrimary,
    ...(party.partyType === "account"
      ? { accountId: party.partyId }
      : { contactId: party.partyId }),
  }));
}

function percentToDecimal(value: string | null | undefined): string | null | undefined {
  if (value == null || value === "") return value;
  const percent = Number.parseFloat(value);
  if (Number.isNaN(percent)) return value;
  return (percent / 100).toFixed(4);
}

function hasScheduleInput(schedule: PricingScheduleFormData): boolean {
  return Boolean(
    schedule.baseRate ||
      schedule.margin ||
      schedule.totalRate ||
      schedule.effectiveFrom ||
      schedule.effectiveTo ||
      schedule.regimeMonths ||
      schedule.repaymentOverride ||
      schedule.amortisationPeriodMonths ||
      schedule.notes ||
      schedule.paymentType !== "interest_only" ||
      schedule.rateType !== "fixed" ||
      schedule.entryMode !== "composed",
  );
}

export function buildFacilityCreatePricingSchedules(
  schedules: PricingScheduleFormData[],
  fallbackEffectiveFrom = new Date().toISOString().slice(0, 10),
): PricingScheduleFormData[] {
  return schedules.filter(hasScheduleInput).map((schedule) => ({
    ...schedule,
    effectiveFrom: schedule.effectiveFrom || fallbackEffectiveFrom,
    baseRate: percentToDecimal(schedule.baseRate) ?? null,
    margin: percentToDecimal(schedule.margin) ?? null,
    totalRate: percentToDecimal(schedule.totalRate) ?? null,
  }));
}
